import { ReactElement, useState, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useNavigate } from 'react-router-dom'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { canRead, canWrite } from 'features/Users/utils'
import styled from 'styled-components'
import { UploadCSVModal } from 'features/OperationalCosts/components/UploadCSVModal'
import { OperationalCostsContainer } from 'features/OperationalCosts/containers/OperationalCostsContainer'
import { OperationalCostsFilters } from 'features/OperationalCosts/components/OperationalCostsFilters'
import { mapFiltersToSearchQueryString, mapSearchToFiltersForm } from 'features/OperationalCosts/utils'
import { getQueries } from 'utils/route'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useBusinessSiteAutocomplete } from 'features/BusinessSites/hooks'
import { IOperationalCostsFilters } from 'features/OperationalCosts/types'
import { useVehicleTemplateAutocomplete } from 'features/VehicleTemplates/hooks'

const messages = defineMessages({
  helmet: {
    id: 'operationalCosts.helmet',
    defaultMessage: 'Operational Costs | Nexbus Admin'
  }
})

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
`

const OperationalCostsView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const navigate = useNavigate()
  const [isOpenUploadCsvModal, setOpenUploadCsvModal] = useState(false)
  const hasPermissions = canRead(FEATURES.OPERATIONAL_COST, login.accessToken.policies)
  const hasWritePermissions = canWrite(FEATURES.OPERATIONAL_COST, login.accessToken.policies)
  const searchQueries = getQueries(location.search)
  const businessClientAutocomplete = useBusinessClientAutocomplete(searchQueries.businessClientId, {
    skip: !searchQueries.businessClientId
  })
  const businessSiteAutocomplete = useBusinessSiteAutocomplete(searchQueries.businessSiteId, {
    skip: !searchQueries.businessSiteId
  })
  const { vehicleTemplate, loading: vehicleTemplateLoading } = useVehicleTemplateAutocomplete(
    searchQueries.vehicleTemplateId,
    {
      skip: !searchQueries.vehicleTemplateId
    }
  )

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [intl])

  if (!hasPermissions) {
    return <ForbiddenView feature={FEATURES.OPERATIONAL_COST} />
  }

  const filters = mapSearchToFiltersForm(searchQueries, {
    businessClient: businessClientAutocomplete,
    businessSite: businessSiteAutocomplete,
    vehicleTemplate
  })

  const handleFiltersSubmit = (values: IOperationalCostsFilters): void => {
    navigate(`/operational-costs?${mapFiltersToSearchQueryString(values)}`)
  }

  return (
    <>
      <Content>
        <ContentHeader>
          <Heading.H3>
            <FormattedMessage id="operationalCosts.header.title" defaultMessage="Operational Costs List" />
          </Heading.H3>
          {hasWritePermissions && (
            <ButtonsContainer>
              <Button kind="primary" type="button" onClick={(): void => setOpenUploadCsvModal(true)}>
                <FormattedMessage id="operationalCosts.uploadCSV" defaultMessage="Upload CSV" />
              </Button>
              <Button as={NavLink} to="/operational-costs/new" kind="primary">
                <FormattedMessage id="operationalCosts.header.add" defaultMessage="+ Add Operational Cost" />
              </Button>
            </ButtonsContainer>
          )}
        </ContentHeader>
        <ContentBody withGap>
          <OperationalCostsFilters filters={filters} onSubmit={handleFiltersSubmit} />
          <section>
            <OperationalCostsContainer filters={filters} vehicleTemplateLoading={vehicleTemplateLoading} />
          </section>
        </ContentBody>
      </Content>
      {isOpenUploadCsvModal && <UploadCSVModal onClose={(): void => setOpenUploadCsvModal(false)} />}
    </>
  )
}

export default withLastUrl(OperationalCostsView)
